/**
 * FWC — standalone World Cup fantasy app.
 *
 * Reuses the princhess WC screen, login, signaling and styles, but runs against
 * a COMPLETELY SEPARATE worker instance (fwc-signal: its own Durable Object,
 * users and data — nothing shared with princhess). No lobby, no bottom nav:
 * signing in lands straight on the World Cup team page.
 */
import './styles.css';
import './fwc.css'; // FWC-only font + accent + login overrides (must load after styles.css)
import { LoginUI } from './ui/login';
import { Signaling } from './net/signaling';
import { WcClient } from './wc/client';
import { KIT_COLORS } from './wc/domain';
import { WcScreen } from './ui/wc';

/** FWC's shield palette = the shared kit colours plus three FWC-only options. */
const FWC_KIT_COLORS = [
  ...KIT_COLORS,
  { name: 'Hot Pink', hex: '#ff2d8d' },
  { name: 'Purple', hex: '#7c3aed' },
  { name: 'Teal', hex: '#14b8a6' },
];
import { applyTheme, loadTheme, watchSystemTheme } from './theme';
import type { SignalMessage, WcSquad } from './net/protocol';

declare const __SIGNALING_URL__: string;

const STORAGE_SESSION = 'fwc.session';
const STORAGE_SQUADS = 'fwc.wcSquads';

interface Session { token: string; userId: string; opponentId: string; exp: number }

function httpFromWsUrl(wsUrl: string): string {
  const u = new URL(wsUrl);
  u.protocol = u.protocol === 'wss:' ? 'https:' : 'http:';
  u.pathname = u.pathname.replace(/\/ws\/?$/, '');
  return u.origin + u.pathname.replace(/\/$/, '');
}

function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_SESSION);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    if (!s.token || !s.userId || !s.opponentId) return null;
    if (typeof s.exp !== 'number' || s.exp < Date.now()) { localStorage.removeItem(STORAGE_SESSION); return null; }
    return s;
  } catch { return null; }
}
function saveSession(s: Session): void { try { localStorage.setItem(STORAGE_SESSION, JSON.stringify(s)); } catch { /* */ } }
function clearSession(): void { try { localStorage.removeItem(STORAGE_SESSION); } catch { /* */ } }

function loadSquads(): WcSquad[] {
  try {
    const raw = localStorage.getItem(STORAGE_SQUADS);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? (parsed as WcSquad[]) : [];
  } catch { return []; }
}
function saveSquads(squads: WcSquad[]): void { try { localStorage.setItem(STORAGE_SQUADS, JSON.stringify(squads)); } catch { /* */ } }

class FwcApp {
  private root = document.getElementById('app') as HTMLElement;
  private session: Session | null = loadSession();
  private signaling: Signaling;
  private wcClient: WcClient | null = null;
  private wcScreen: WcScreen | null = null;
  private squads: WcSquad[] = loadSquads();

  constructor() {
    applyTheme(loadTheme());
    watchSystemTheme();
    this.signaling = new Signaling({
      token: () => this.session?.token ?? null,
      activeGameId: () => undefined, // FWC has no live games to resume
      appState: () => (document.visibilityState === 'visible' ? 'active' : 'background'),
      onMessage: (msg) => this.onMessage(msg),
    });
    if (this.session) this.renderWc();
    else this.renderLogin();
  }

  private onMessage(msg: SignalMessage): void {
    switch (msg.t) {
      case 'registered':
        // Server's userId is canonical (in case the local copy was stale).
        if (this.session && msg.userId !== this.session.userId) {
          this.session.userId = msg.userId;
          saveSession(this.session);
        }
        this.signaling.send({ t: 'wc-squads-request' });
        break;
      case 'wc-squads':
        this.squads = msg.squads;
        saveSquads(msg.squads);
        this.wcScreen?.setSquads(msg.squads);
        break;
      case 'wc-save-rejected':
        this.wcScreen?.onSaveRejected(msg.reason);
        break;
      case 'error':
        // The only fatal error for FWC is a bad/expired token at register.
        if (/auth|token|unauthor/i.test(msg.reason)) {
          this.session = null;
          clearSession();
          this.renderLogin();
        }
        break;
      default:
        break; // chess/uno/etc. traffic — not used by FWC
    }
  }

  private renderLogin(): void {
    this.wcScreen?.destroy();
    this.wcScreen = null;
    new LoginUI(this.root, { onSubmit: (u, p) => this.attemptLogin(u, p) }, 'FWC');
  }

  private async attemptLogin(username: string, password: string): Promise<string | null> {
    try {
      const res = await fetch(httpFromWsUrl(__SIGNALING_URL__) + '/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        try {
          const data = (await res.json()) as { error?: string };
          return data.error || 'Sign-in failed';
        } catch { return 'Sign-in failed'; }
      }
      const data = (await res.json()) as Session;
      this.session = data;
      saveSession(data);
      this.renderWc();
      this.signaling.reconnect(); // register with the fresh token
      return null;
    } catch {
      return 'Network error — try again.';
    }
  }

  /** Straight into the World Cup screen — no lobby, no nav. */
  private renderWc(): void {
    if (!this.session) return;
    this.wcScreen?.destroy();
    this.root.innerHTML = '<div class="fantasy-hub"><div class="fantasy-body" data-fwc-body></div></div>';
    const body = this.root.querySelector('[data-fwc-body]') as HTMLElement;
    if (!this.wcClient) this.wcClient = new WcClient(httpFromWsUrl(__SIGNALING_URL__));
    this.wcScreen = new WcScreen(
      body,
      this.wcClient,
      {
        onSave: (squad) => {
          this.signaling.send({ t: 'wc-save', squad });
          // Optimistic local cache — the server broadcast confirms shortly.
          this.squads = [...this.squads.filter((s) => s.userId !== squad.userId), squad];
          saveSquads(this.squads);
        },
        onRequestSquads: () => this.signaling.send({ t: 'wc-squads-request' }),
      },
      { youId: this.session.userId, opponentId: this.session.opponentId, squads: this.squads, kitColors: FWC_KIT_COLORS, budgetCap: 150 },
    );
  }
}

new FwcApp();
